import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/wineshope";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const wineshope: CaseStudy = {
  slug: "wineshope",
  title: "WineShope",
  cardTitle: "Разработка интернет-магазина винного ритейлера WineShope",
  subtitle: "Винный ритейл",
  industry: { label: "Еда и продукты", slug: "food" },
  eyebrow: "Интернет-магазин · Винный ритейл",
  h1: "Разработка интернет-магазина WineShope",
  lead:
    "Разработали интернет-магазин для винного ритейлера WineShope: масштабный каталог с фасетной фильтрацией по странам и производителям и алфавитной навигацией по брендам.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить интернет-магазин" },
  siteUnavailable: true,

  seo: {
    primaryKeyword: "разработка интернет-магазина вина",
    secondaryKeywords: [
      "сайт винного магазина разработка",
      "e-commerce для винного бутика",
      "разработка каталога вина",
      "сайт алкогольного ритейла",
    ],
    semanticEntities: [
      "фасетная фильтрация",
      "каталог по странам",
      "каталог по производителям",
      "алфавитная навигация",
    ],
    excludedKeywords: [
      "купить вино",
      "вино доставка",
      "красное вино цена",
      "магазин алкоголя",
    ],
    title: "Интернет-магазин WineShope — кейс HAY.agency",
    description:
      "Кейс HAY.agency: e-commerce винного магазина WineShope с масштабным каталогом, фильтрацией по странам и производителям и алфавитной навигацией по брендам.",
    h1: "Разработка интернет-магазина WineShope",
    slug: "wineshope",
    canonicalUrl: `${SITE_URL}/cases/wineshope`,
    ogTitle: "Интернет-магазин винного ритейлера WineShope",
    ogDescription:
      "Масштабный каталог вина с фасетной фильтрацией по странам, производителям и алфавитной навигацией по брендам.",
    ogImage: `${SITE_URL}${dir}/wineshope-og.webp`,
    robotsIndex: true,
    datePublished: "2020-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка и развитие интернет-магазинов",
      to: routes.ecom,
      note: "Каталог, фильтрация и навигация по ассортименту",
    },
    relatedIndustry: {
      label: "SEO-продвижение интернет-магазинов",
      to: routes.seo,
      note: "Видимость каталога и категорий",
    },
    relatedCases: [
      {
        label: "E-commerce платформа для производителя косметики Levrana",
        to: "/cases/levrana",
        note: "Мультибрендовый каталог с фильтрацией",
      },
      {
        label: "Разработка интернет-магазина цветов «Цветочный маркет»",
        to: "/cases/cvmarket",
        note: "Каталог, карточка товара и корзина",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен интернет-магазин с большим каталогом?",
    text: "Спроектируем фасетную фильтрацию и навигацию по ассортименту для крупного каталога.",
    button: "Обсудить интернет-магазин",
  },

  client: {
    name: "WineShope",
    url: "http://wineshope.ru/",
    city: "Москва",
    industry: "Винный ритейл",
  },
  year: "С 2020 года",
  services: [
    "Дизайн интернет-магазина",
    "Разработка каталога с фасетной фильтрацией",
    "Алфавитная навигация по производителям",
  ],
  platform: "PrestaShop",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "Разработка", to: routes.ecom },
  ],
  facts: [
    { label: "Клиент", value: "WineShope" },
    { label: "Отрасль", value: "Винный ритейл" },
    { label: "Тип проекта", value: "Интернет-магазин с масштабным каталогом" },
    { label: "Платформа", value: "PrestaShop" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Масштабный каталог с фасетной фильтрацией по странам и производителям",
      "Алфавитная навигация по производителям среди сотен брендов",
      "Счётчики количества товаров в фильтрах",
      "Каталог, рассчитанный на десятки стран и сотни брендов",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "WineShope — винный ритейлер с масштабным ассортиментом из десятков стран и сотен производителей. Нужен был каталог, в котором покупатель быстро находит вино по стране или бренду, не теряясь в объёме.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: винная подача каталога",
    paragraphs: [
      "Интерфейс собран на приглушённой винно-графитовой подложке, которая держит фокус на карточках товара, а не на фоне.",
      "Фасетные фильтры и алфавитный указатель работают как два основных инструмента навигации по большому ассортименту.",
    ],
    bullets: [
      "Приглушённая винно-графитовая подложка",
      "Фасетная фильтрация по странам и производителям",
      "Алфавитная навигация по брендам",
    ],
  },

  identity: {
    fonts: [
      { name: "Cormorant", role: "Заголовки", sample: "Каталог WineShope" },
      { name: "Cormorant", role: "Интерфейс каталога", sample: "Фильтр по стране" },
    ],
    colors: [
      { hex: "#2B2320", name: "Фон витрины" },
      { hex: "#7A2E38", name: "Акцент бренда" },
      { hex: "#EFE7DD", name: "Текст на тёмном" },
    ],
  },

  cover: img("wineshope-case-cover", "Главная страница интернет-магазина WineShope", 6),

  gallery: [
    img("wineshope-filters", "Фасетная фильтрация по странам в каталоге WineShope", 6),
    img("wineshope-brands", "Алфавитная навигация по производителям WineShope", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия повторяет логику десктопа: фильтрация по странам и производителям остаётся доступной в один переход.",
      "Состояния каталога в мобильной версии показаны заполненными.",
    ],
    images: [
      mobile("wineshope-mobile-hero", "Мобильная главная интернет-магазина WineShope"),
      mobile("wineshope-mobile-filters", "Мобильные фильтры каталога WineShope"),
    ],
  },

  columnBlocks: [
    {
      id: "catalog",
      title: "Масштаб каталога",
      paragraphs: [
        "Главный продуктовый акцент — фасетные фильтры по странам и производителям с количеством товаров в каждой категории.",
      ],
      columns: [
        { title: "Страны", items: ["Десятки стран-производителей"] },
        { title: "Бренды", items: ["Сотни производителей"] },
      ],
    },
    {
      id: "navigation",
      title: "Навигация по ассортименту",
      paragraphs: [
        "Алфавитный указатель производителей дополняет фильтры и ускоряет поиск конкретного бренда в большом каталоге.",
      ],
      bullets: ["Алфавитная навигация A–Z", "Счётчики товаров в фильтрах"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [{ label: "Разработка интернет-магазина", to: routes.ecom }],
  },
};
